import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Paper,
  Typography,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { FilterConfig } from '../../hooks/useSimulations';

interface SimulationFiltersProps {
  filterConfig: FilterConfig;
  showFilters: boolean;
  onFilterChange: (key: keyof FilterConfig, value: any) => void;
  onToggleFilters: () => void;
  onClearFilters: () => void;
  availablePlatforms?: string[];
  availableReputationLevels?: string[];
  availableConfidenceLevels?: string[];
}

const SimulationFilters: React.FC<SimulationFiltersProps> = ({
  filterConfig,
  showFilters,
  onFilterChange,
  onToggleFilters,
  onClearFilters,
  availablePlatforms = ['Twitter/X', 'Facebook', 'Instagram', 'LinkedIn', 'TikTok'],
  availableReputationLevels = ['Strong Uplift', 'Mild Uplift', 'Neutral', 'Mild Damage', 'Severe Damage'],
  availableConfidenceLevels = ['Low', 'Medium', 'High']
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const activeFilterCount = Object.values(filterConfig).filter(v => v).length;

  return (
    <>
      {/* Filter Controls Section */}
      <Box 
        component="section" 
        aria-label="Simulations Controls" 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: 2, 
          gap: { xs: 2, sm: 0 }
        }}
      >
        <Typography variant="h6" component="h2">
          All Simulations
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<FilterListIcon />} 
          onClick={onToggleFilters}
          aria-expanded={showFilters}
          aria-controls="filters-panel"
          fullWidth={isMobile}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'} ({activeFilterCount})
        </Button>
      </Box>

      <Collapse in={showFilters}>
        <Paper component="aside" id="filters-panel" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
          <Typography variant="subtitle1" component="h3" gutterBottom>Filter by:</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Content Filters</Typography>
              <Divider sx={{ mb: 2, mt: 0.5 }} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Platform</InputLabel>
                <Select
                  value={filterConfig.platform || ''}
                  label="Platform"
                  onChange={(e) => onFilterChange('platform', e.target.value)}
                >
                  <MenuItem value="">Any</MenuItem>
                  {availablePlatforms.map(platform => (
                    <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Event Type"
                value={filterConfig.eventType || ''}
                onChange={(e) => onFilterChange('eventType', e.target.value)}
                placeholder="Enter event type"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Account"
                value={filterConfig.account || ''}
                onChange={(e) => onFilterChange('account', e.target.value)}
                placeholder="Search by account name"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Sector"
                value={filterConfig.sector || ''}
                onChange={(e) => onFilterChange('sector', e.target.value)}
                placeholder="Search by sector"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Created By"
                value={filterConfig.createdBy || ''}
                onChange={(e) => onFilterChange('createdBy', e.target.value)}
                placeholder="Search by name"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <FormControl sx={{ flex: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filterConfig.hasImage === true}
                        indeterminate={filterConfig.hasImage === undefined}
                        onChange={(e) => {
                          if (filterConfig.hasImage === true) {
                            onFilterChange('hasImage', undefined);
                          } else if (filterConfig.hasImage === undefined) {
                            onFilterChange('hasImage', false);
                          } else {
                            onFilterChange('hasImage', true);
                          }
                        }}
                      />
                    }
                    label="Has Image"
                  />
                </FormControl>
                
                <FormControl sx={{ flex: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filterConfig.isPrivate === true}
                        indeterminate={filterConfig.isPrivate === undefined}
                        onChange={(e) => {
                          if (filterConfig.isPrivate === true) {
                            onFilterChange('isPrivate', undefined);
                          } else if (filterConfig.isPrivate === undefined) {
                            onFilterChange('isPrivate', false);
                          } else {
                            onFilterChange('isPrivate', true);
                          }
                        }}
                      />
                    }
                    label="Is Private"
                  />
                </FormControl>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Results Filters</Typography>
              <Divider sx={{ mb: 2, mt: 0.5 }} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Reputation Impact</InputLabel>
                <Select
                  value={filterConfig.reputationImpact || ''}
                  label="Reputation Impact"
                  onChange={(e) => onFilterChange('reputationImpact', e.target.value)}
                >
                  <MenuItem value="">Any</MenuItem>
                  {availableReputationLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Confidence</InputLabel>
                <Select
                  value={filterConfig.confidence || ''}
                  label="Confidence"
                  onChange={(e) => onFilterChange('confidence', e.target.value)}
                >
                  <MenuItem value="">Any</MenuItem>
                  {availableConfidenceLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Date Filters</Typography>
              <Divider sx={{ mb: 2, mt: 0.5 }} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Created After"
                type="date"
                size="small"
                fullWidth
                value={filterConfig.createdAfter || ''}
                onChange={(e) => onFilterChange('createdAfter', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Created Before"
                type="date"
                size="small"
                fullWidth
                value={filterConfig.createdBefore || ''}
                onChange={(e) => onFilterChange('createdBefore', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                startIcon={<ClearIcon />}
                onClick={onClearFilters}
                disabled={Object.keys(filterConfig).length === 0}
                variant="outlined"
                color="primary"
                fullWidth={isMobile}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
    </>
  );
};

export default SimulationFilters;